import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
} from 'react';

const PDF_ZOOM_MIN = 0.5;
const PDF_ZOOM_MAX = 2;
const PDF_ZOOM_STEP = 0.1;

interface PdfPreviewContextValue {
  url: string;
  page: number;
  pages: number;
  zoom: number;
  containerWidth: number | null;
  toolbarHeight: number;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  pageRefs: MutableRefObject<Record<number, HTMLDivElement | null>>;
  setPages: (numPages: number) => void;
  setPage: (page: number) => void;
  goToPage: (page: number) => void;
  changeZoom: (nextZoom: number) => void;
}

const PdfPreviewContext = createContext<PdfPreviewContextValue | null>(null);

export const usePdfPreview = () => {
  const value = useContext(PdfPreviewContext);

  if (!value) {
    throw new Error('usePdfPreview must be used within <PdfPreviewProvider>');
  }

  return value;
};

/** Owns all PDF viewer state and behaviour; PdfPreview and the toolbar controls consume it. */
export const PdfPreviewProvider = ({ url, children }: { url: string; children: ReactNode }) => {
  const [page, setPage] = useState(1);
  const [pages, setPagesState] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    setPage(1);
    setPagesState(0);
    setZoom(1);
    pageRefs.current = {};
  }, [url]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      const width = scrollContainerRef.current?.clientWidth;

      if (width && width > 0) {
        setContainerWidth(width);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const element = scrollContainerRef.current;

    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [url]);

  useLayoutEffect(() => {
    // The sticky page-level toolbar overlaps the scroll top; offset page jumps by its height.
    const element = document.querySelector('.share-preview-controls');

    if (!element) {
      return;
    }

    const updateHeight = () => setToolbarHeight(element.getBoundingClientRect().height);

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [pages]);

  useEffect(() => {
    if (pages === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        const visiblePage = Number(mostVisible?.target.getAttribute('data-page-number'));

        if (visiblePage) {
          setPage(visiblePage);
        }
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );

    Object.values(pageRefs.current).forEach((element) => element && observer.observe(element));

    return () => observer.disconnect();
  }, [pages]);

  const setPages = (numPages: number) => {
    setPagesState(numPages);
    setPage((prev) => Math.min(prev, numPages || 1));
  };

  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    pageRefs.current[nextPage]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changeZoom = (nextZoom: number) => {
    const clampedZoom = Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, +nextZoom.toFixed(2)));
    const container = scrollContainerRef.current;

    if (container && clampedZoom !== zoom) {
      const ratio = clampedZoom / zoom;
      const targetScrollLeft = (container.scrollLeft + container.clientWidth / 2) * ratio - container.clientWidth / 2;
      const targetScrollTop = (container.scrollTop + container.clientHeight / 2) * ratio - container.clientHeight / 2;

      requestAnimationFrame(() => {
        container.scrollLeft = targetScrollLeft;
        container.scrollTop = targetScrollTop;
      });
    }

    setZoom(clampedZoom);
  };

  return (
    <PdfPreviewContext.Provider
      value={{
        url,
        page,
        pages,
        zoom,
        containerWidth,
        toolbarHeight,
        scrollContainerRef,
        pageRefs,
        setPages,
        setPage,
        goToPage,
        changeZoom,
      }}
    >
      {children}
    </PdfPreviewContext.Provider>
  );
};

interface PdfNavButtonProps {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}

const PdfNavButton = ({ direction, disabled, onClick }: PdfNavButtonProps) => (
  <button
    type='button'
    className={`general-button ${direction === 'prev' ? 'total-contributions-prev' : 'total-contributions-next'}`}
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
    style={{ width: '22px', height: '22px' }}
  >
    <span className={direction === 'prev' ? 'icon-arrow-back' : 'icon-arrow-next'} />
  </button>
);

/** Zoom controls for the page-level toolbar. Renders nothing until a PDF has loaded. */
export const PdfZoomControls = () => {
  const { pages, zoom, changeZoom } = usePdfPreview();

  if (pages === 0) {
    return null;
  }

  return (
    <div className='d-none d-lg-flex align-items-center border specific-y-padding-5 specific-x-padding-4 h-100 rounded-pill total-contributions-buttons bg-white'>
      <button
        type='button'
        className='general-button d-flex align-items-center justify-content-center paragraph-p3'
        disabled={zoom <= PDF_ZOOM_MIN}
        onClick={() => changeZoom(zoom - PDF_ZOOM_STEP)}
        aria-label='Zoom out'
        style={{ width: '22px', height: '22px' }}
      >
        <span className='d-inline-block-centered centered-4'>−</span>
      </button>
      <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>{Math.round(zoom * 100)}%</span>
      <button
        type='button'
        className='general-button d-flex align-items-center justify-content-center paragraph-p3'
        disabled={zoom >= PDF_ZOOM_MAX}
        onClick={() => changeZoom(zoom + PDF_ZOOM_STEP)}
        aria-label='Zoom in'
        style={{ width: '22px', height: '22px' }}
      >
        <span className='d-inline-block-centered centered-4'>+</span>
      </button>
    </div>
  );
};

export const PdfPageControls = () => {
  const { page, pages, goToPage } = usePdfPreview();

  if (pages === 0) {
    return null;
  }

  return (
    <div className='d-flex align-items-center border specific-top-padding-4 specific-bottom-padding-5 specific-x-padding-8 h-100 rounded-pill total-contributions-buttons bg-white'>
      <PdfNavButton direction='prev' disabled={page <= 1} onClick={() => goToPage(page - 1)} />
      <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>
        Page {page} of {pages}
      </span>
      <PdfNavButton direction='next' disabled={page >= pages} onClick={() => goToPage(page + 1)} />
    </div>
  );
};
